<template>
  <NativeDrawer v-model="drawer" :before-close="handleDrawerClose" size="80%">
    <template #header>
      <h4>专家团队信息</h4>
    </template>
    <template #default>
      <div class="doctor-content">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <NativeSkeleton :rows="5" animated />
        </div>

        <!-- 专家列表 -->
        <div v-else class="doctor-list">
          <!-- 搜索筛选组件 -->
          <BaseSearch
            :search-form="searchForm"
            :fields="searchFields"
            @search="handleSearch"
            @reset="handleReset"
          />

          <!-- 表格组件 -->
          <DoctorTable
            :doctor-list="doctorList"
            @edit="handleEdit"
            @delete="handleDeleteConfirm"
          />

          <!-- 分页组件 -->
          <BasePagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <!-- 编辑表单组件 -->
        <DoctorForm
          v-model:visible="editDialogVisible"
          :is-edit="isEdit"
          :form-data="formData"
          :submit-loading="submitLoading"
          @submit="handleSubmitForm"
          @cancel="handleFormCancel"
        />
      </div>
    </template>
    <template #footer>
      <div
        style="flex: auto; display: flex; justify-content: flex-end; gap: 10px"
      >
        <NativeButton @click="addClick" type="success">新增医生</NativeButton>
        <NativeButton type="primary" @click="confirmClick">关闭</NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import { useDoctorsListStore, type Doctor } from "@/stores/doctors";
import { useDepartmentsStore } from "@/stores/departments";
import { notify } from "@/utils/nativeFeedback";

import type { SearchField } from "@/components/NativeUI/types";

// 导入子组件
import BaseSearch from "./BaseSearch/index.vue";
import DoctorTable from "./doctor/DoctorTable.vue";
import BasePagination from "./BasePagination/index.vue";
import DoctorForm from "./doctor/DoctorForm.vue";

// ==================== Props & Emits ====================
const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();
const doctorsStore = useDoctorsListStore();
const departmentsStore = useDepartmentsStore();

// ==================== Drawer 状态 ====================
const drawer = ref(props.visible);

watch(
  () => props.visible,
  (newVal) => {
    drawer.value = newVal;
    if (newVal) {
      loadDoctorList();
    }
  },
);

watch(drawer, (newVal) => {
  emit("update:visible", newVal);
});

// ==================== 数据相关 ====================
const loading = ref(false);
const allDoctorList = ref<Doctor[]>([]);
const doctorList = ref<Doctor[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 搜索表单
const searchForm = ref({
  name: "",
  title: "",
  specialty: "",
});

// 筛选选项
const titleOptions = computed(() => {
  const titles = new Set(
    allDoctorList.value.map((item) => item.title).filter(Boolean),
  );
  return Array.from(titles);
});

const specialtyOptions = computed(() => {
  const specialties = new Set(
    allDoctorList.value.map((item) => item.specialty).filter(Boolean),
  );
  return Array.from(specialties);
});

// 搜索字段配置
const searchFields = computed<SearchField[]>(() => [
  {
    label: "姓名",
    prop: "name",
    type: "input",
    placeholder: "请输入姓名",
    width: "200px",
  },
  {
    label: "职称",
    prop: "title",
    type: "select",
    placeholder: "请选择职称",
    width: "150px",
    options: titleOptions.value.map((item) => ({ label: item, value: item })),
  },
  {
    label: "科室",
    prop: "specialty",
    type: "select",
    placeholder: "请选择科室",
    width: "150px",
    options: specialtyOptions.value.map((item) => ({
      label: item,
      value: item,
    })),
  },
]);

// 筛选后的数据
const filteredList = computed(() => {
  let list = allDoctorList.value;

  if (searchForm.value.name) {
    list = list.filter((item) => item.name?.includes(searchForm.value.name));
  }

  if (searchForm.value.title) {
    list = list.filter((item) => item.title === searchForm.value.title);
  }

  if (searchForm.value.specialty) {
    list = list.filter((item) => item.specialty === searchForm.value.specialty);
  }

  return list;
});

// 编辑表单
const editDialogVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const formData = ref<Partial<Doctor>>({
  id: 0,
  name: "",
  title: "",
  specialty: "",
  experience: "",
  badge: "",
  expertiseList: [{ expertise: "", sortOrder: 0 }],
  major: true,
  type: 0,
  avatar: "",
  category: "",
});

// ==================== API 调用 ====================
const updatePageData = () => {
  const list = filteredList.value;
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  doctorList.value = list.slice(start, end);
  total.value = list.length;
};

const getDepartmentList = () => {
  const list = departmentsStore.getDepartmentsList;
  const map: Record<string, string> = {};
  list.forEach((item) => {
    map[item.name] = item.category;
  });

  map["门诊内科"] = "emergency";
  map["急诊内科"] = "emergency";
  return map;
};

const loadDoctorList = async () => {
  loading.value = true;
  try {
    await Promise.all([
      doctorsStore.refreshDoctors(),
      departmentsStore.refreshDepartments(),
    ]);
    allDoctorList.value = doctorsStore.allDoctors;
    updatePageData();
    localStorage.setItem("deptMap", JSON.stringify(getDepartmentList()));
  } catch (error) {
    console.error("加载专家列表失败:", error);
    notify("加载专家列表失败", "error");
  } finally {
    loading.value = false;
  }
};

// ==================== 事件处理 ====================
const handleSearch = () => {
  currentPage.value = 1;
  updatePageData();
};

const handleReset = () => {
  searchForm.value = {
    name: "",
    title: "",
    specialty: "",
  };
  currentPage.value = 1;
  updatePageData();
};

const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  updatePageData();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  updatePageData();
};

const handleEdit = (row: Doctor) => {
  isEdit.value = true;
  formData.value = {
    id: row.id,
    name: row.name || "",
    title: row.title || "",
    specialty: row.specialty || "",
    experience: row.experience || "",
    badge: row.badge || "",
    expertiseList: row.expertiseList || [{ expertise: "", sortOrder: 1 }],
    major: row.major ?? true,
    type: row.type ?? 0,
    avatar: row.avatar || "",
    category: row.category || "",
  };
  editDialogVisible.value = true;
};

const handleDeleteConfirm = async (row: Doctor) => {
  if (!window.confirm(`确认删除专家 "${row.name}" 吗?`)) return;

  if (await doctorsStore.deleteDoctor(row.id)) {
    allDoctorList.value = doctorsStore.allDoctors;
    updatePageData();
  }
};

const handleSubmitForm = async (data: {
  formData: Partial<Doctor>;
  originalData: Partial<Doctor>;
}) => {
  submitLoading.value = true;
  try {
    const success =
      isEdit.value && data.formData.id
        ? await doctorsStore.updateDoctorById(data.formData.id, data.formData)
        : await doctorsStore.createDoctor(data.formData);

    if (success) {
      allDoctorList.value = doctorsStore.allDoctors;
      updatePageData();
      editDialogVisible.value = false;
    }
  } finally {
    submitLoading.value = false;
  }
};

const handleFormCancel = () => {
  formData.value = {
    id: 0,
    name: "",
    title: "",
    specialty: "",
    experience: "",
    badge: "",
    expertiseList: [{ expertise: "", sortOrder: 0 }],
    major: true,
    type: 0,
    avatar: "",
    category: "",
  };
  isEdit.value = false;
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

const confirmClick = () => {
  drawer.value = false;
};

const addClick = () => {
  isEdit.value = false;
  formData.value = {
    id: 0,
    name: "",
    title: "",
    specialty: "",
    experience: "",
    badge: "",
    expertiseList: [{ expertise: "", sortOrder: 0 }],
    major: true,
    type: 0,
    avatar: "",
    category: "",
  };
  editDialogVisible.value = true;
};

// 监听筛选结果变化
watch(filteredList, () => {
  currentPage.value = 1;
  updatePageData();
});
</script>

<style scoped lang="scss">
.doctor-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .loading-container {
    padding: 20px;
  }

  .doctor-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    // 搜索筛选区域 - 固定在顶部
    :deep(.search-filter) {
      flex-shrink: 0;
      position: sticky;
      top: 0;
      z-index: 10;
      background-color: #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    // 表格区域 - 可滚动
    :deep(.native-table) {
      flex: 1;
      overflow: auto;
    }

    // 分页组件 - 固定在底部
    :deep(.pagination-container) {
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      z-index: 10;
      background-color: #fff;
      //   padding: 15px 0 20px 0;
      border-top: 1px solid #ebeef5;
      margin-top: 0;
    }
  }
}
</style>
